import { debounce, type DebouncedFunc, type DebounceSettings } from 'lodash';
import { useCallback, useEffect, useRef } from 'react';

import useEvent from './useEvent';

const DEFAULT_TIMEOUT = 500;

/**
 * creates a debounced stable version of the passed handler function, which cleans up after itself
 *
 * do NOT call returned handler during render
 */
const useDebouncedHandler = <TArgs extends unknown[], TReturn>(
  handler: (...args: TArgs) => TReturn,
  timeout = DEFAULT_TIMEOUT,
  settings: DebounceSettings = {},
) => {
  const { leading, maxWait, trailing } = settings;

  const debouncedHandlerRef = useRef<DebouncedFunc<typeof handler> | null>(null);

  const stableHandler = useEvent(handler);

  useEffect(() => {
    // only include defined settings to avoid breaking lodash default values
    // we can't have settings as a dependency since it might not be stable
    const debounceOptions: DebounceSettings = {};

    if (leading !== undefined) debounceOptions.leading = leading;
    if (maxWait !== undefined) debounceOptions.maxWait = maxWait;
    if (trailing !== undefined) debounceOptions.trailing = trailing;

    const debouncedHandler = debounce(stableHandler, timeout, debounceOptions);

    debouncedHandlerRef.current = debouncedHandler;

    return () => debouncedHandler.cancel();
  }, [leading, maxWait, stableHandler, timeout, trailing]);
  // the whole point of useEvent is that the handler shouldn't be a dependency of the effect, but ESLint doesn't know this
  // just include it, since it's stable it shouldn't hurt

  return useCallback((...args: TArgs) => debouncedHandlerRef.current?.(...args), []);
};

export default useDebouncedHandler;
